/**
 * Sanitizes and formats any PIX Key (CPF, CNPJ, Phone, Email, UUID) strictly according
 * to Banco Central do Brasil (BCB) DICT standard.
 */
export function sanitizePixKey(rawKey: string, keyType?: string): string {
  if (!rawKey) return "";
  let key = rawKey.trim();

  // 1. Check if key is a UUID (Chave Aleatória / EVP)
  const uuidMatch = key.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  if (uuidMatch) {
    return uuidMatch[0].toLowerCase();
  }

  // 2. Check if key is email (contains @)
  if (key.includes("@")) {
    const emailMatch = key.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) {
      return emailMatch[0].toLowerCase();
    }
    return key.toLowerCase();
  }

  // 3. Extract all digits
  const digitsOnly = key.replace(/\D/g, "");

  // 4. CPF (11 digits without punctuation)
  if (keyType === "cpf" || digitsOnly.length === 11) {
    if (digitsOnly.length === 11) {
      return digitsOnly;
    }
  }

  // 5. CNPJ (14 digits without punctuation)
  if (keyType === "cnpj" || digitsOnly.length === 14) {
    if (digitsOnly.length === 14) {
      return digitsOnly;
    }
  }

  // 6. Phone (Must start with +55 for BCB DICT)
  if (
    keyType === "phone" ||
    key.startsWith("+") ||
    (digitsOnly.length >= 10 && digitsOnly.length <= 11 && (key.includes("(") || key.includes("-")))
  ) {
    let phoneDigits = digitsOnly;
    if (!phoneDigits.startsWith("55") && (phoneDigits.length === 10 || phoneDigits.length === 11)) {
      phoneDigits = "55" + phoneDigits;
    }
    return "+" + phoneDigits;
  }

  // 7. Fallbacks based on digit length
  if (digitsOnly.length === 11) {
    return digitsOnly;
  }
  if (digitsOnly.length === 14) {
    return digitsOnly;
  }

  return key;
}

export function validateCpfCnpj(cpfCnpj: string): { isValid: boolean; clean: string; type: "cpf" | "cnpj" | "invalid" } {
  const clean = cpfCnpj ? cpfCnpj.replace(/\D/g, "") : "";
  if (clean.length === 11) {
    return { isValid: true, clean, type: "cpf" };
  }
  if (clean.length === 14) {
    return { isValid: true, clean, type: "cnpj" };
  }
  return { isValid: false, clean, type: "invalid" };
}

export function isSandboxApiKey(apiKey?: string | null): boolean {
  if (!apiKey) return false;
  const clean = apiKey.trim();
  if (!clean.startsWith("$aact_")) return false;
  const lower = clean.toLowerCase();
  return (
    lower.startsWith("$aact_hmlg_") ||
    lower.startsWith("$aact_h_") ||
    lower.includes("hmlg") ||
    lower.includes("sandbox") ||
    lower.includes("mock")
  );
}

export function isProductionApiKey(apiKey?: string | null): boolean {
  if (!apiKey) return false;
  const clean = apiKey.trim();
  if (!clean.startsWith("$aact_")) return false;
  if (isSandboxApiKey(clean)) return false;
  return clean.toLowerCase().startsWith("$aact_prod_") || clean.startsWith("$aact_");
}

export function validateAsaasApiKeyFormat(apiKey?: string | null): boolean {
  if (!apiKey || apiKey.trim().length < 10) return false;
  return isSandboxApiKey(apiKey) || isProductionApiKey(apiKey);
}
